import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { StudentInfo } from "../student/studentSlice";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface ClassroomStudentsState {
    state: LoadStateInfo;
    request?: RetrieveClassroomStudentsRequest;
    list?: StudentInfo[];
}

const initialState: ClassroomStudentsState = {
    state: notStarted(),
};

export interface RetrieveClassroomStudentsRequest {
    schoolId?: string;
    classroomId?: string;
}
export const retrieveClassroomStudents = createAsyncThunk(
    "retrieveClassroomStudents",
    async (params: RetrieveClassroomStudentsRequest, thunkAPI) => {
        console.debug(`retrieve students for classroom ${params.classroomId} at school ${params.schoolId}`);

        const response = await fetch(`${getBackendUrl()}/api/classroom/${params.classroomId}/student?getFamily=true`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveClassroomStudentsRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.classroomStudents;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveClassroomStudents skipped: ${status.state.state}`);
            return false;
        }
    }
);


export const classroomStudentsSlice = createSlice({
    name: "classroomStudents",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        add: (state, action: PayloadAction<StudentInfo>) => {
            if (state.list) {
                state.list.push(action.payload);
                state.list.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            } else {
                console.error("Trying to add student to an empty list");
            }
        },
        remove: (state, action: PayloadAction<StudentInfo>) => {
            if (state.list) {
                state.list = state.list.filter((item) => item.id !== action.payload.id);
            } else {
                console.error("Trying to remove student from an empty list");
            }
        },
        check: (state, action: PayloadAction<RetrieveClassroomStudentsRequest>) => {
            const data = action.payload;
            const request = state.request;
            console.log(`Check ${data.schoolId} != ${request?.schoolId}, ${data.classroomId} != ${request?.classroomId}`);
            if (`${request?.schoolId}` != `${data.schoolId}`
                || `${request?.classroomId}` != `${data.classroomId}`
            ) {
                console.log("changed");
                state.state = notStarted();
                state.list = undefined;
            } else {
                console.log(`unchanged :: ${state.state.state}`);
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveClassroomStudents.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveClassroomStudents.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            })
            .addCase(retrieveClassroomStudents.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

// export const { } = classroomStudentsSlice.actions;

export const selectClassroomStudents = (state: RootState) => state.classroomStudents;

export default classroomStudentsSlice.reducer;
